import { useState, useEffect, useRef, useCallback } from 'react';

export interface TickPoint {
  i: number;
  price: number;
}

export interface GoldPriceState {
  price: number | null;
  bid: number | null;
  ask: number | null;
  change: number | null;
  changePct: number | null;
  series: TickPoint[];
  lastUpdated: Date | null;
  loading: boolean;
  error: boolean;
}

const TICK_LENGTH = 36;
const POLL_INTERVAL = 30_000; // 30 s
const SPREAD = 0.18;
const ENDPOINT = 'https://api.gold-api.com/price/XAU';

/** Generate a synthetic backward walk so the chart has history before the first real tick */
function seedSeries(basePrice: number, count: number): TickPoint[] {
  const ticks: TickPoint[] = [];
  let p = basePrice;
  for (let i = count - 1; i >= 0; i--) {
    const noise = (Math.random() - 0.48) * 0.6;
    p = Math.max(1000, p - noise);
    ticks.unshift({ i, price: parseFloat(p.toFixed(2)) });
  }
  return ticks;
}

function appendTick(prev: TickPoint[], price: number): TickPoint[] {
  const next = [...prev.slice(-TICK_LENGTH + 1), { i: (prev[prev.length - 1]?.i ?? 0) + 1, price }];
  return next.map((t, idx) => ({ ...t, i: idx }));
}

export function useGoldPrice(): GoldPriceState {
  const [state, setState] = useState<GoldPriceState>({
    price: null,
    bid: null,
    ask: null,
    change: null,
    changePct: null,
    series: [],
    lastUpdated: null,
    loading: true,
    error: false,
  });

  const openRef = useRef<number | null>(null); // first price of the session
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPrice = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINT, { cache: 'no-store' });
      if (!res.ok) throw new Error('non-200');
      const data: { price?: number; updatedAt?: string } = await res.json();
      const raw = data?.price;
      if (!raw || typeof raw !== 'number') throw new Error('no gold field');

      const price = parseFloat(raw.toFixed(2));
      const bid = parseFloat((price - SPREAD / 2).toFixed(2));
      const ask = parseFloat((price + SPREAD / 2).toFixed(2));

      setState((prev) => {
        // Seed the chart history on the very first successful response
        const series =
          prev.series.length === 0
            ? seedSeries(price, TICK_LENGTH)
            : appendTick(prev.series, price);

        // Lock the opening price for session change calculation
        if (openRef.current === null) openRef.current = price;
        const open = openRef.current;
        const change = parseFloat((price - open).toFixed(2));
        const changePct = parseFloat(((change / open) * 100).toFixed(3));

        return {
          price,
          bid,
          ask,
          change,
          changePct,
          series,
          lastUpdated: data.updatedAt ? new Date(data.updatedAt) : new Date(),
          loading: false,
          error: false,
        };
      });
    } catch {
      setState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, []);

  useEffect(() => {
    fetchPrice();
    timerRef.current = setInterval(fetchPrice, POLL_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchPrice]);

  return state;
}